import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["moduleId", "as", "verticalPositioning", "popoverPlacement", "children", "collaborationSidebarState", "onUpdateCollaborationSidebarState", "showIndicator", "showIndicatorsOnHover", "iconPosition", "iconProps", "renderCommentIndicator", "metaDescription", "userInfo"];
import { useState, useEffect, useCallback, useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import useEmbeddedComments from 'collaboration-sidebar/features/deepCommenting/hooks/useEmbeddedComments';
import useButtonA11yProps from 'collaboration-sidebar/hooks/useButtonA11yProps';
import useCommentableAreaStyles from 'collaboration-sidebar/features/deepCommenting/hooks/useCommentableAreaStyles';
import useUpdateMetaDescriptions from 'collaboration-sidebar/features/deepCommenting/hooks/useUpdateMetaDescriptions';
import { COMMENTABLE_AREA_IDENTIFIER } from 'collaboration-sidebar/constants/identifiers';
import EmbeddedComment from './EmbeddedComment';
import { useToggleOpenPopover } from '../hooks/useToggleOpenPopover';
import { useEmbeddedCommentHandlers } from '../hooks/useEmbeddedCommentHandlers';
import useElementResizeObserver from '../../../hooks/useElementResizeObserver';
import useWindowScrollListener from '../../../hooks/useWindowScrollListener';
import { useEmbeddedCommentChangeHandlers } from '../hooks/useEmbeddedCommentChangeHandlers';
import { useEmbeddedCommentClickListener } from '../hooks/useEmbeddedCommentClickListener';
import usePrevious from '../../../hooks/usePrevious';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TD_ELEMENT_TYPE = 'TD';
const StyledHoverableCommentableArea = styled.div.withConfig({
  displayName: "EmbeddedCommentableArea__StyledHoverableCommentableArea",
  componentId: "zatb7s-0"
})([".hs-private.hs-private.hs-private &{outline-color:rgba(255,122,89,0) !important;position:relative !important;", " ", " ", " ", " ", " ", " ", "}"], ({
  outlineRadius
}) => outlineRadius && `border-radius: ${outlineRadius}px;`, ({
  showComments,
  isCommentModeActive
}) => (showComments || isCommentModeActive) && `
    transition: outline-color 0.15s ease-in, box-shadow 0.15s ease-in;
    outline: 2px solid rgba(255, 122, 89, 0) !important;
    pointer-events: auto !important;

    > td {
      pointer-events: auto !important;
    }
  `, ({
  isHighlightedBySidebar,
  outlineRadius
}) => isHighlightedBySidebar && (outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'), ({
  isTableChild,
  isDefaultElement
}) => isTableChild && isDefaultElement && `
    outline-offset: -2px;
    display: table;
    width: 100%;
  `, ({
  isTr
}) => isTr && `
    outline-offset: -2px;
  `, ({
  isCommentModeActive,
  outlineRadius
}) => isCommentModeActive && `
    &:hover, &:focus {
      cursor: crosshair;

      ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}

      *:not(#comment-indicator) {
        pointer-events: none;
      }
    }
  `, ({
  isCommentModeActive,
  isCommenting,
  outlineRadius
}) => isCommentModeActive && isCommenting && `
    ${outlineRadius ? 'box-shadow: 0 0 0 2px rgb(255 122 89);' : 'outline-color: rgba(255, 122, 89, 1) !important;'}
      &:hover {
        cursor: default;
      }
    `);
function EmbeddedCommentableArea(_ref) {
  let {
      moduleId,
      as = 'div',
      verticalPositioning = false,
      popoverPlacement,
      children,
      collaborationSidebarState,
      onUpdateCollaborationSidebarState,
      showIndicator = true,
      showIndicatorsOnHover = false,
      iconPosition,
      iconProps,
      renderCommentIndicator,
      metaDescription,
      userInfo
    } = _ref,
    propsForWrapper = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [commentableArea, setCommentableArea] = useState(null);
  const [embeddedCommentWrapper, setEmbeddedCommentWrapper] = useState(null);
  const [autoOpenCommentPopover, setAutoOpenCommentPopover] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const onRemoveComment = useCallback(() => setIsHovering(false), []);
  const {
    isEmbeddedPopoverOpen,
    embeddedModuleID
  } = collaborationSidebarState;
  const isOpen = isEmbeddedPopoverOpen && embeddedModuleID === moduleId;
  const prevIsOpen = usePrevious(isOpen);
  const commentableAreaRef = useCallback(node => {
    setCommentableArea(node);
  }, []);
  const embeddedCommentWrapperRef = useCallback(node => {
    setEmbeddedCommentWrapper(node);
  }, []);
  const {
    isCommentModeActive,
    showComments,
    isCommenting,
    setIsCommenting,
    broadcastCommentsMessage,
    isHighlightedBySidebar,
    moduleParentComment
  } = useEmbeddedComments({
    moduleId,
    collaborationSidebarState,
    onUpdateCollaborationSidebarState,
    onRemoveComment,
    commentableArea,
    userInfo
  });
  const prevIsCommentModeActive = usePrevious(isCommentModeActive);
  const {
    ctaUrl,
    objectType
  } = collaborationSidebarState;
  const {
    isTableChild,
    isTr
  } = useCommentableAreaStyles({
    commentableArea,
    children
  });
  useUpdateMetaDescriptions({
    userInfo,
    metaDescription,
    moduleParentComment,
    ctaUrl,
    broadcastCommentsMessage
  });
  const {
    updateIndicatorPlacement,
    updateEmbeddedCommentCoordinates,
    placement,
    topOffset,
    leftOffset
  } = useEmbeddedCommentHandlers({
    commentableArea,
    popoverPlacement,
    verticalPositioning,
    embeddedCommentWrapper,
    onUpdateCollaborationSidebarState
  });
  const toggleOpenPopover = useToggleOpenPopover({
    embeddedCommentWrapper,
    onUpdateCollaborationSidebarState,
    moduleId,
    metaDescription,
    placement
  });
  const handleResize = useCallback(() => {
    updateIndicatorPlacement();
    updateEmbeddedCommentCoordinates();
  }, [updateEmbeddedCommentCoordinates, updateIndicatorPlacement]);

  // Force popover to auto open on hoverable area click in comment mode.
  useEffect(() => {
    let autoOpenPopoverTimer;
    if (isCommenting) {
      toggleOpenPopover(true, true);
      autoOpenPopoverTimer = setTimeout(() => {
        setAutoOpenCommentPopover(true);
      }, 0);
    }
    return () => clearTimeout(autoOpenPopoverTimer);
  }, [isCommenting, toggleOpenPopover]);
  useEffect(() => {
    let setIsCommentingTimeout;
    if (!isOpen) {
      setIsCommenting(false);
    }
    return () => clearTimeout(setIsCommentingTimeout);
  }, [isOpen, setIsCommenting]);

  // Turn off commenting hover indicators on close of the popover.
  const onClosePopover = useCallback(() => {
    setIsCommenting(false);
    setAutoOpenCommentPopover(false);
    toggleOpenPopover(false);
  }, [setIsCommenting, toggleOpenPopover]);
  const onCommentableAreaClick = useCallback(e => {
    if (showComments || isCommentModeActive) {
      // Only start commenting on the target initially clicked.
      // This is to avoid opening popovers for nested commentable areas.
      const target = e.target instanceof HTMLElement ? e.target : null;
      const parentNode = (target === null || target === void 0 ? void 0 : target.parentNode) instanceof HTMLElement ? target.parentNode : null;
      const currentTarget = e.currentTarget instanceof HTMLElement ? e.currentTarget : null;
      if (target && currentTarget && parentNode) {
        const isActiveCommentableArea = currentTarget.id === target.id || target.nodeName === TD_ELEMENT_TYPE && parentNode.id === currentTarget.id;
        if (isCommentModeActive && !isCommenting && isActiveCommentableArea) {
          setIsCommenting(true);
        } else if (isCommenting && isActiveCommentableArea) {
          e.stopPropagation();
        }
      }
    }
  }, [isCommentModeActive, isCommenting, setIsCommenting, showComments]);
  const showEmbeddedComment = useMemo(() => {
    if (!objectType || !showIndicator) {
      return false;
    }
    const showIndicatorsFromHovering = showIndicatorsOnHover && (isHovering || isCommenting);
    return showIndicatorsFromHovering || showComments;
  }, [objectType, isCommenting, isHovering, showComments, showIndicator, showIndicatorsOnHover]);
  const onClickHandler = useCallback(e => {
    onCommentableAreaClick(e);
    if (!isCommentModeActive && propsForWrapper.onClick) {
      propsForWrapper.onClick(e);
    }
  }, [isCommentModeActive, onCommentableAreaClick, propsForWrapper]);
  const buttonA11yProps = useButtonA11yProps({
    i18nKey: 'collaborationSidebar.modals.commentMode.leaveComment',
    allowTabbing: isCommentModeActive,
    onClick: onClickHandler
  });
  useEffect(() => {
    updateIndicatorPlacement();
  }, [moduleId, commentableArea, updateIndicatorPlacement]);
  useElementResizeObserver(commentableArea, handleResize);
  useWindowScrollListener({
    listenToScroll: isOpen,
    onScroll: updateEmbeddedCommentCoordinates,
    useCapture: true
  });
  useEmbeddedCommentChangeHandlers({
    isCommentModeActive,
    prevIsCommentModeActive,
    toggleOpenPopover
  });
  useEmbeddedCommentClickListener({
    onUpdateCollaborationSidebarState,
    isOpen,
    prevIsOpen
  });
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized classNames have been checked
    _jsxs(StyledHoverableCommentableArea, Object.assign({
      id: `commentable-area-${moduleId}`,
      className: classnames(COMMENTABLE_AREA_IDENTIFIER, isCommenting && 'commenting-commentable-area'),
      isCommentModeActive: isCommentModeActive,
      isCommenting: isCommenting,
      isTableChild: isTableChild,
      isTr: isTr,
      showComments: showComments,
      ref: commentableAreaRef,
      as: as,
      isDefaultElement: as === 'div',
      isHighlightedBySidebar: isHighlightedBySidebar,
      onMouseEnter: () => setIsHovering(true),
      onMouseLeave: () => setIsHovering(false)
    }, buttonA11yProps, propsForWrapper, {
      onClick: onClickHandler,
      children: [children, showEmbeddedComment && /*#__PURE__*/_jsx(EmbeddedComment, {
        moduleId: moduleId,
        isOpenFromCommentMode: autoOpenCommentPopover,
        onClose: onClosePopover,
        commentableAreaRef: commentableAreaRef,
        collaborationSidebarState: collaborationSidebarState,
        verticalPositioning: verticalPositioning || isTr,
        popoverPlacement: placement,
        iconPosition: iconPosition,
        iconProps: iconProps,
        renderCommentIndicator: renderCommentIndicator,
        setIsCommenting: setIsCommenting,
        metaDescription: metaDescription,
        moduleParentComment: moduleParentComment,
        userInfo: userInfo,
        onUpdateCollaborationSidebarState: onUpdateCollaborationSidebarState,
        topOffset: topOffset,
        leftOffset: leftOffset,
        isOpen: isOpen,
        embeddedCommentWrapperRef: embeddedCommentWrapperRef,
        toggleOpenPopover: toggleOpenPopover
      })]
    }))
  );
}
EmbeddedCommentableArea.propTypes = {
  moduleId: PropTypes.string.isRequired,
  className: PropTypes.string,
  as: PropTypes.string,
  children: PropTypes.node,
  verticalPositioning: PropTypes.bool,
  collaborationSidebarState: PropTypes.object.isRequired,
  onUpdateCollaborationSidebarState: PropTypes.func.isRequired,
  popoverPlacement: PropTypes.string,
  showIndicatorsOnHover: PropTypes.bool,
  iconPosition: PropTypes.object,
  iconProps: PropTypes.object,
  outlineRadius: PropTypes.number,
  userInfo: PropTypes.shape({
    user: PropTypes.object,
    portal: PropTypes.object
  }),
  metaDescription: PropTypes.shape({
    translationKey: PropTypes.string.isRequired,
    options: PropTypes.object
  })
};
export default EmbeddedCommentableArea;